import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MoveDown } from "lucide-react";
import { useRef } from "react";
import { CV_URL } from "../data";

const HERO_IMG =
  "https://images.unsplash.com/photo-1686806372785-fcfe9efa9b70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwcGFwZXIlMjB0ZXh0dXJlJTIwY2VyYW1pYyUyMGFyY2hpdGVjdHVyYWwlMjBkZXRhaWwlMjB3YXJtJTIwYmVpZ2V8ZW58MHx8fHwxNzkzNzIzMDg0fDA&ixlib=rb-4.1.0&q=85";

const line = {
  hidden: { y: "110%", rotate: 2 },
  visible: {
    y: "0%",
    rotate: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};

const MaskedLine = ({ index, children }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block will-change-transform"
      initial="hidden"
      animate="visible"
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.09, delayChildren: 0.45 + index * 0.16 } },
      }}
    >
      <motion.span className="block" variants={line}>
        {children}
      </motion.span>
    </motion.span>
  </span>
);

const IllustrativeTag = () => (
  <span className="absolute top-2.5 right-3 text-[9px] font-mono uppercase tracking-widest text-muted/70">
    illustrative
  </span>
);

const clueBase =
  "absolute bg-surface border border-line rounded-xl shadow-[0_24px_50px_-24px_rgba(28,32,29,0.35)] p-4";

const RunOfShowCard = ({ delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 28, rotate: -7 }}
    animate={{ opacity: 1, y: 0, rotate: -4 }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay }}
    className={`${clueBase} top-0 left-0 w-56 sm:w-64 z-10`}
    data-testid="hero-clue-runofshow"
  >
    <IllustrativeTag />
    <p className="text-[10px] font-mono uppercase tracking-widest text-terra">Run of show</p>
    <p className="mt-1 font-serif text-sm text-ink">800 guests · one evening</p>
    <div className="mt-3 space-y-1.5 text-[11px] font-mono text-ink2">
      {[
        ["17:40", "Doors open"],
        ["18:00", "Opening"],
        ["18:20", "Act I"],
        ["19:10", "Act II"],
      ].map(([t, e]) => (
        <div key={t} className="flex gap-3 border-b border-line/70 pb-1">
          <span className="text-muted w-9 shrink-0">{t}</span>
          <span>{e}</span>
        </div>
      ))}
      <div className="flex gap-3 pt-1 text-terra">
        <span className="w-9 shrink-0">22:40</span>
        <span className="font-medium">Venue — replaced overnight</span>
      </div>
    </div>
  </motion.div>
);

const BadgeCard = ({ delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 28, rotate: 6 }}
    animate={{ opacity: 1, y: 0, rotate: 3 }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay }}
    className={`${clueBase} bottom-0 left-6 sm:left-14 w-48 sm:w-56 z-20 bg-terrasoft/60`}
    data-testid="hero-clue-badges"
  >
    <IllustrativeTag />
    <p className="text-[10px] font-mono uppercase tracking-widest text-terra">Pre-order campaign</p>
    <div className="mt-2 flex items-center gap-3">
      <div className="w-14 h-14 rounded-full border-2 border-terra/70 flex items-center justify-center shrink-0">
        <span className="font-serif italic text-[13px] text-terra leading-none text-center">
          EPOCH
          <br />
          &rsquo;20
        </span>
      </div>
      <div>
        <p className="font-serif text-2xl text-ink leading-none">318+</p>
        <p className="mt-1 text-[10px] font-mono uppercase tracking-wider text-muted">
          badges pre-sold
        </p>
      </div>
    </div>
    <div className="mt-3 flex items-end gap-1.5 h-8" aria-hidden="true">
      {[35, 55, 45, 80, 100, 70].map((h, i) => (
        <div key={i} className="flex-1 bg-terra/50 rounded-sm" style={{ height: `${h}%` }} />
      ))}
    </div>
    <p className="mt-2 text-[10px] font-mono text-muted">Sold first · produced after · zero waste</p>
  </motion.div>
);

const ResearchCard = ({ delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 28, rotate: 5 }}
    animate={{ opacity: 1, y: 0, rotate: 2 }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay }}
    className={`${clueBase} top-20 sm:top-14 right-0 w-60 sm:w-72 z-30`}
    style={{
      backgroundImage:
        "linear-gradient(#F3EFEA 1px, transparent 1px), linear-gradient(90deg, #F3EFEA 1px, transparent 1px)",
      backgroundSize: "22px 22px",
      backgroundColor: "#FFFFFF",
    }}
    data-testid="hero-clue-research"
  >
    <IllustrativeTag />
    <p className="text-[10px] font-mono uppercase tracking-widest text-terra">Research notes</p>
    <p className="mt-1 font-serif text-sm text-ink leading-snug">
      Academic stress × memory retrieval — an exploratory study
    </p>
    <svg viewBox="0 0 200 70" className="mt-3 w-full" aria-hidden="true">
      <line x1="8" y1="62" x2="192" y2="62" stroke="#828A84" strokeWidth="0.75" />
      <line x1="8" y1="62" x2="8" y2="6" stroke="#828A84" strokeWidth="0.75" />
      <line x1="14" y1="52" x2="186" y2="18" stroke="#B84A39" strokeWidth="1" strokeDasharray="3 3" />
      {[
        [30, 48], [52, 44], [70, 50], [88, 38], [105, 40], [122, 33], [140, 28], [160, 24],
      ].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2.4" fill="#B84A39" fillOpacity="0.75" />
      ))}
      <text x="150" y="69" fontSize="6" fill="#828A84" fontFamily="monospace">stress →</text>
      <text x="2" y="6" fontSize="6" fill="#828A84" fontFamily="monospace">retrieval</text>
    </svg>
    <p className="mt-2 text-[10px] font-mono text-muted">surveys · interviews · exploratory</p>
  </motion.div>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const clueY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-24 sm:pt-28">
      <img
        src={HERO_IMG}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-parchment/70 via-parchment/50 to-parchment" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center py-10 sm:py-14">
          <div className="lg:col-span-6">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="text-xs sm:text-sm font-mono uppercase tracking-[0.22em] text-terra mb-6"
              data-testid="hero-tagline"
            >
              MSc Psychology · German Culture · Project Delivery
            </motion.p>

            <h1
              data-testid="hero-headline"
              className="font-serif text-4xl sm:text-5xl lg:text-[3.6rem] tracking-tight leading-[1.1] text-ink"
            >
              <MaskedLine index={0}>I&rsquo;m Irene —</MaskedLine>
              <MaskedLine index={1}>
                I turn{" "}
                <em className="text-terra font-serif not-italic font-semibold underline decoration-terra/40 decoration-2 underline-offset-8">
                  complex projects
                </em>
              </MaskedLine>
              <MaskedLine index={2}>into clear next steps.</MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="mt-6 max-w-md text-base sm:text-lg font-sans text-ink leading-relaxed"
              data-testid="hero-description"
            >
              An 800-person event saved overnight. A funding gap closed with 318 badges. A
              dissertation exploring how academic stress relates to memory retrieval. The cases
              below show how I work.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.3 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <a
                href="#work"
                data-testid="hero-cta-explore-work"
                className="group inline-flex items-center gap-2 bg-terra text-parchment font-sans font-medium text-sm sm:text-base rounded-full px-7 py-3.5 hover:bg-terrahover transition-colors duration-300"
              >
                Explore my work
                <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" aria-hidden="true" />
              </a>
              <a
                href={CV_URL}
                download="Irene-Chen-CV.pdf"
                data-testid="hero-cta-download-cv"
                className="inline-flex items-center gap-2 border border-ink/40 text-ink font-sans font-medium text-sm sm:text-base rounded-full px-7 py-3.5 hover:border-terra hover:text-terra transition-colors duration-300"
              >
                Download CV
              </a>
            </motion.div>

            <motion.a
              href="#work"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.8 }}
              className="mt-10 hidden lg:inline-flex items-center gap-3 text-xs font-mono uppercase tracking-[0.2em] text-muted hover:text-terra transition-colors duration-300"
              data-testid="hero-scroll-cue"
            >
              <MoveDown className="w-3.5 h-3.5 animate-bounce" aria-hidden="true" />
              Scroll — the first case is just below
            </motion.a>
          </div>

          <motion.div className="lg:col-span-6" style={{ y: clueY }}>
            <div className="relative h-[300px] sm:h-[380px] lg:h-[440px] max-w-lg mx-auto lg:max-w-none">
              <RunOfShowCard delay={0.7} />
              <ResearchCard delay={0.85} />
              <BadgeCard delay={1.0} />
            </div>
          </motion.div>
        </div>

        <motion.a
          href="#detail-orientation-programme"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.6 }}
          data-testid="hero-first-case-preview"
          className="group flex items-center gap-4 sm:gap-6 border-t border-line py-5 sm:py-6"
        >
          <img
            src="/images/orientation-2019.jpg"
            alt="Group photo from the 2019 Inter-Organisation Orientation Programme"
            className="w-16 h-12 sm:w-20 sm:h-14 object-cover rounded-md border border-line shrink-0"
          />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] sm:text-xs font-mono uppercase tracking-widest text-terra">
              Case 01 · Event Logistics &amp; Crisis Management
            </p>
            <p className="mt-1 font-serif text-base sm:text-lg text-ink truncate group-hover:text-terra transition-colors duration-300">
              Inter-Organisation Orientation Programme — 800+ participants, venue replaced overnight
            </p>
          </div>
          <ArrowDown
            className="w-5 h-5 text-muted group-hover:text-terra transition-all duration-300 group-hover:translate-y-1 shrink-0"
            aria-hidden="true"
          />
        </motion.a>
      </div>
    </section>
  );
};

export default Hero;
